import { useEffect } from "react";
import {
  TextOverlay,
  LogoOverlay,
  // WatermarkOverlay,
  SlateOverlay,
  DragState,
  ResizeState,
  TextResizeState,
  TextEditingState,
  LogoEditingState,
  // WatermarkEditingState,
} from "../types/overlayTypes";
import { TextRotationState, InlineTextEditingState } from "./useOverlayState";
import {
  calculateTextDimensions,
  createTextContext,
  calculateRotatedCorners,
  isPointInRotatedRectangle,
} from "../utils/textCalculations";

export const useOverlayInteractions = (
  canvasRef: React.RefObject<HTMLCanvasElement>,
  textOverlays: TextOverlay[],
  logoOverlays: LogoOverlay[],
  // watermarkOverlays: WatermarkOverlay[],
  slateOverlays: SlateOverlay[],
  dragState: DragState,
  resizeState: ResizeState,
  textResizeState: TextResizeState,
  textRotationState: TextRotationState,
  // textEditingState: TextEditingState,
  inlineTextEditingState: InlineTextEditingState,
  // logoEditingState: LogoEditingState,
  // watermarkEditingState: WatermarkEditingState,
  showCanvasOverlay: boolean,
  showTextModal: boolean,
  // showLogoModal: boolean,
  // showWatermarkModal: boolean,
  setDragState: (state: DragState) => void,
  setResizeState: (state: ResizeState) => void,
  setTextResizeState: (state: TextResizeState) => void,
  setTextRotationState: (state: TextRotationState) => void,
  setTextEditingState: (state: TextEditingState) => void,
  setInlineTextEditingState: (state: InlineTextEditingState) => void,
  setLogoEditingState: (state: LogoEditingState) => void,
  // setWatermarkEditingState: (state: WatermarkEditingState) => void,
  setShowTextModal: (show: boolean) => void,
  setShowLogoModal: (show: boolean) => void,
  // setShowWatermarkModal: (show: boolean) => void,
  updateTextOverlay: (
    id: string,
    updates: Partial<TextOverlay>,
    constrainPosition?: boolean
  ) => void,
  updateLogoOverlay: (id: string, updates: Partial<LogoOverlay>) => void,
  // updateWatermarkOverlay: (id: string, updates: Partial<WatermarkOverlay>) => void,
  updateSlateOverlay: (id: string, updates: Partial<SlateOverlay>) => void,
  onTextOverlaySelect?: (textOverlayId: string) => void,
  onLogoOverlaySelect?: (logoOverlayId: string) => void,
  handleTextEditCancel?: () => void,
  // handleLogoEditCancel?: () => void,
  // handleWatermarkEditCancel?: () => void,
  videoDimensions?: { width: number; height: number }, // New parameter
  renderCanvas?: () => void, // Add renderCanvas function as parameter
  contentNonEditable?: boolean, // New parameter to disable interactions
  readOnlyMode?: boolean, // New parameter to disable interactions
  maintainAspectRatio?: boolean
) => {
  // Utility functions and constants
  const RESIZE_HANDLE_SIZE = 8;
  const RESIZE_HANDLE_HIT_SIZE = 20;
  const CORNER_CURSORS = {
    "top-left": "nw-resize",
    "top-right": "ne-resize",
    "bottom-left": "sw-resize",
    "bottom-right": "se-resize",
  } as const;

  const getCornerPositions = (
    elementX: number,
    elementY: number,
    logoWidth: number,
    logoHeight: number
  ) => [
    {
      x: elementX - logoWidth / 2,
      y: elementY - logoHeight / 2,
      corner: "top-left" as const,
    },
    {
      x: elementX + logoWidth / 2,
      y: elementY - logoHeight / 2,
      corner: "top-right" as const,
    },
    {
      x: elementX - logoWidth / 2,
      y: elementY + logoHeight / 2,
      corner: "bottom-left" as const,
    },
    {
      x: elementX + logoWidth / 2,
      y: elementY + logoHeight / 2,
      corner: "bottom-right" as const,
    },
  ];

  const isPointInCorner = (
    pointX: number,
    pointY: number,
    cornerX: number,
    cornerY: number,
    handleSizePercent: number
  ) => {
    return (
      Math.abs(pointX - cornerX) < handleSizePercent &&
      Math.abs(pointY - cornerY) < handleSizePercent
    );
  };

  const isPointInLogo = (
    pointX: number,
    pointY: number,
    logoX: number,
    logoY: number,
    logoWidth: number,
    logoHeight: number
  ) => {
    return (
      pointX >= logoX - logoWidth / 2 &&
      pointX <= logoX + logoWidth / 2 &&
      pointY >= logoY - logoHeight / 2 &&
      pointY <= logoY + logoHeight / 2
    );
  };

  const isPointInRotationHandle = (
    pointX: number,
    pointY: number,
    elementX: number,
    elementY: number,
    elementHeight: number,
    rotation: number
  ) => {
    const handleDistance = elementHeight / 2 + 30; // 30px above the element
    const angle = (rotation * Math.PI) / 180;

    // Calculate the rotated position
    const rotatedX = elementX + handleDistance * Math.sin(angle);
    const rotatedY = elementY - handleDistance * Math.cos(angle);

    const handleSize = 8; // Size of the rotation handle
    const distance = Math.sqrt(
      (pointX - rotatedX) ** 2 + (pointY - rotatedY) ** 2
    );

    return distance <= handleSize;
  };

  const isPointInRotationHandleCanvas = (
    pointX: number,
    pointY: number,
    elementX: number,
    elementY: number,
    elementHeight: number,
    rotation: number,
    scaleX: number,
    scaleY: number,
    offsetX: number,
    offsetY: number
  ) => {
    const handleDistance = elementHeight / 2 + 30; // 30px above the element
    const angle = (rotation * Math.PI) / 180;

    // Calculate the rotated position in canvas coordinates
    const rotatedX = elementX + handleDistance * Math.sin(angle);
    const rotatedY = elementY - handleDistance * Math.cos(angle);

    const handleSize = 15; // Increased from 8 to 15 for easier detection
    const distance = Math.sqrt(
      (pointX - rotatedX) ** 2 + (pointY - rotatedY) ** 2
    );

    return distance <= handleSize;
  };

  const handleCursorMove = (e: React.MouseEvent) => {
    if (!showCanvasOverlay || !canvasRef.current || readOnlyMode) {
      return;
    }

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const videoElement = document.querySelector("video");
    const videoWidth =
      videoDimensions?.width || videoElement?.videoWidth || 1920;
    const videoHeight =
      videoDimensions?.height || videoElement?.videoHeight || 1080;
    const canvasAspectRatio = rect.width / rect.height;
    const videoAspectRatio = videoWidth / videoHeight;

    let scaleX = 1;
    let scaleY = 1;
    let offsetX = 0;
    let offsetY = 0;

    if (canvasAspectRatio > videoAspectRatio) {
      scaleY = rect.height / videoHeight;
      scaleX = scaleY;
      offsetX = (rect.width - videoWidth * scaleX) / 2;
    } else {
      scaleX = rect.width / videoWidth;
      scaleY = scaleX;
      offsetY = (rect.height - videoHeight * scaleY) / 2;
    }
    const videoX = (x - offsetX) / scaleX;
    const videoY = (y - offsetY) / scaleY;
    const percentX = Math.round((videoX / videoWidth) * 100);
    const percentY = Math.round((videoY / videoHeight) * 100);

    // Calculate the actual video area dimensions on the canvas
    const videoAreaWidth = videoWidth * scaleX;
    const videoAreaHeight = videoHeight * scaleY;

    // Helper function to convert video coordinates to screen coordinates
    const videoToScreenCoords = (videoX: number, videoY: number) => {
      // Use the actual video content area dimensions, not the entire canvas
      const screenX = (videoX / 100) * videoAreaWidth + offsetX;
      const screenY = (videoY / 100) * videoAreaHeight + offsetY;
      return { x: screenX, y: screenY };
    };

    // Helper function to convert percentage coordinates to canvas coordinates
    const getCanvasPosition = (percentX: number, percentY: number) => {
      const videoX = (percentX / 100) * videoWidth;
      const videoY = (percentY / 100) * videoHeight;
      return {
        x: videoX * scaleX + offsetX,
        y: videoY * scaleY + offsetY,
      };
    };

    // Helper function to check if a point is within an overlay area
    const isPointInOverlayArea = (
      pointX: number,
      pointY: number,
      overlayX: number,
      overlayY: number,
      overlayWidth: number,
      overlayHeight: number
    ) => {
      const tolerance = 50;
      return (
        Math.abs(pointX - overlayX) < overlayWidth / 2 + tolerance &&
        Math.abs(pointY - overlayY) < overlayHeight / 2 + tolerance
      );
    };

    // Check for text resize corner hover first (highest priority)
    for (const textOverlay of textOverlays) {
      if (textOverlay.visible && textOverlay.text) {
        const textX = textOverlay.position.x;
        const textY = textOverlay.position.y;

        // Convert to canvas coordinates first (same as canvas rendering)
        const textCanvasPosition = getCanvasPosition(textX, textY);

        // Calculate text dimensions in canvas coordinates (same as canvas rendering)
        const fontSize = textOverlay.fontSize;
        const padding = (textOverlay.padding || 0) * Math.min(scaleX, scaleY);
        const maxWidth = videoWidth * Math.min(scaleX, scaleY);

        const tempCtx = createTextContext(textOverlay, fontSize);
        if (!tempCtx) continue;

        const { paddedWidth, paddedHeight } = calculateTextDimensions(
          tempCtx,
          textOverlay.text || "New Text",
          fontSize,
          padding,
          maxWidth
        );

        const backgroundX = textCanvasPosition.x - paddedWidth / 2 - 8;
        const backgroundY = textCanvasPosition.y - paddedHeight / 2 - 4;
        const backgroundWidth = paddedWidth + 16;
        const backgroundHeight = paddedHeight + 8;

        const corners = [
          { x: backgroundX, y: backgroundY }, // top-left
          { x: backgroundX + backgroundWidth, y: backgroundY }, // top-right
          { x: backgroundX, y: backgroundY + backgroundHeight }, // bottom-left
          {
            x: backgroundX + backgroundWidth,
            y: backgroundY + backgroundHeight,
          }, // bottom-right
        ];

        // Check if mouse is over any corner
        const hoveredCorner = corners.find(
          ({ x: cornerX, y: cornerY }, index) => {
            const distance = Math.sqrt((x - cornerX) ** 2 + (y - cornerY) ** 2);
            return distance <= RESIZE_HANDLE_SIZE / 2;
          }
        );

        if (hoveredCorner) {
          const cornerNames = [
            "top-left",
            "top-right",
            "bottom-left",
            "bottom-right",
          ] as const;
          const cornerIndex = corners.indexOf(hoveredCorner);
          canvas.style.cursor = CORNER_CURSORS[cornerNames[cornerIndex]];
          return;
        }

        const screenCoords = videoToScreenCoords(textX, textY);
        const cursorBackgroundWidth = paddedWidth + 16;
        const cursorBackgroundHeight = paddedHeight + 8;
        const cursorRotation = textOverlay.rotation || 0;

        const isInside = isPointInRotatedRectangle(
          x,
          y,
          screenCoords.x,
          screenCoords.y,
          cursorBackgroundWidth,
          cursorBackgroundHeight,
          cursorRotation
        );

        if (isInside) {
          canvas.style.cursor = "move";
          return;
        }
      }
    }

    const allImageOverlays = [...logoOverlays, ...slateOverlays];
    for (const overlay of allImageOverlays) {
      if (overlay.visible && overlay.url) {
        const overlayX = overlay.position.x;
        const overlayY = overlay.position.y;
        const overlayWidth = (overlay.size.width / videoWidth) * 100;
        const overlayHeight = (overlay.size.height / videoHeight) * 100;
        const handleSizePercent = (RESIZE_HANDLE_HIT_SIZE / videoWidth) * 100;

        const corners = getCornerPositions(
          overlayX,
          overlayY,
          overlayWidth,
          overlayHeight
        );
        const hoveredCorner = corners.find(({ x: cornerX, y: cornerY }) =>
          isPointInCorner(
            percentX,
            percentY,
            cornerX,
            cornerY,
            handleSizePercent
          )
        );

        if (hoveredCorner) {
          canvas.style.cursor = CORNER_CURSORS[hoveredCorner.corner];
          return;
        }
        const screenCoords = videoToScreenCoords(overlayX, overlayY);
        const overlayScreenWidth = (overlayWidth / 100) * videoAreaWidth;
        const overlayScreenHeight = (overlayHeight / 100) * videoAreaHeight;

        if (
          isPointInOverlayArea(
            x,
            y,
            screenCoords.x,
            screenCoords.y,
            overlayScreenWidth,
            overlayScreenHeight
          )
        ) {
          canvas.style.cursor = "move";
          return;
        }
      }
    }

    canvas.style.cursor = dragState.isDragging
      ? "grabbing"
      : showCanvasOverlay
      ? "grab"
      : "default";
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!canvasRef.current || !showCanvasOverlay || readOnlyMode) {
      return;
    }

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Get video dimensions for proper coordinate scaling
    const videoElement = document.querySelector("video");
    const videoWidth =
      videoDimensions?.width || videoElement?.videoWidth || 1920;
    const videoHeight =
      videoDimensions?.height || videoElement?.videoHeight || 1080;

    // Calculate scaling factors for proper coordinate conversion
    const canvasAspectRatio = rect.width / rect.height;
    const videoAspectRatio = videoWidth / videoHeight;

    let scaleX = 1;
    let scaleY = 1;
    let offsetX = 0;
    let offsetY = 0;

    if (canvasAspectRatio > videoAspectRatio) {
      // Canvas is wider than video (letterboxing)
      scaleY = rect.height / videoHeight;
      scaleX = scaleY;
      offsetX = (rect.width - videoWidth * scaleX) / 2;
    } else {
      // Canvas is taller than video (pillarboxing)
      scaleX = rect.width / videoWidth;
      scaleY = scaleX;
      offsetY = (rect.height - videoHeight * scaleY) / 2;
    }

    // Calculate the actual video area dimensions on the canvas
    const videoAreaWidth = videoWidth * scaleX;
    const videoAreaHeight = videoHeight * scaleY;

    // Convert canvas coordinates to video coordinates
    const videoX = (x - offsetX) / scaleX;
    const videoY = (y - offsetY) / scaleY;
    const percentX = Math.round((videoX / videoWidth) * 100);
    const percentY = Math.round((videoY / videoHeight) * 100);

    // Helper function to convert video coordinates to screen coordinates
    const videoToScreenCoords = (videoX: number, videoY: number) => {
      // Use the actual video content area dimensions, not the entire canvas
      const screenX = (videoX / 100) * videoAreaWidth + offsetX;
      const screenY = (videoY / 100) * videoAreaHeight + offsetY;
      return { x: screenX, y: screenY };
    };

    // Helper function to convert percentage coordinates to canvas coordinates
    const getCanvasPosition = (percentX: number, percentY: number) => {
      const videoX = (percentX / 100) * videoWidth;
      const videoY = (percentY / 100) * videoHeight;
      return {
        x: videoX * scaleX + offsetX,
        y: videoY * scaleY + offsetY,
      };
    };

    // Helper function to check if a point is within an overlay area (using screen coordinates)
    const isPointInOverlayArea = (
      pointX: number,
      pointY: number,
      overlayX: number,
      overlayY: number,
      overlayWidth: number,
      overlayHeight: number
    ) => {
      const tolerance = 50; // Increased tolerance for better drag detection, especially for overlays outside video area
      return (
        Math.abs(pointX - overlayX) < overlayWidth / 2 + tolerance &&
        Math.abs(pointY - overlayY) < overlayHeight / 2 + tolerance
      );
    };

    // Check text overlays first (highest priority for resize handles)
    // Reverse the order to check from last to first, so that overlays rendered on top are checked first
    // This ensures that when overlays overlap, the topmost one is selected
    const textOverlaysToCheck = [...textOverlays].reverse();
    for (const textOverlay of textOverlaysToCheck) {
      if (
        textOverlay.visible &&
        textOverlay.text !== undefined &&
        textOverlay.text !== null
      ) {
        const textX = textOverlay.position.x;
        const textY = textOverlay.position.y;

        // Convert to canvas coordinates first (same as canvas rendering)
        const textCanvasPosition = getCanvasPosition(textX, textY);

        // Calculate text dimensions in canvas coordinates (same as canvas rendering)
        const fontSize = textOverlay.fontSize;
        const padding = (textOverlay.padding || 0) * Math.min(scaleX, scaleY);

        const maxWidth = videoWidth * Math.min(scaleX, scaleY);

        const textForCalculation = textOverlay.text || "New Text";
        const tempCtx = createTextContext(textOverlay, fontSize);
        if (!tempCtx) continue;

        const { paddedWidth, paddedHeight } = calculateTextDimensions(
          tempCtx,
          textForCalculation,
          fontSize,
          padding,
          maxWidth
        );

        // Calculate background rectangle coordinates (exactly like canvas rendering)
        const textBackgroundWidth = paddedWidth + 16;
        const textBackgroundHeight = paddedHeight + 8;

        // Check for rotation handle clicks first
        const textRotation = textOverlay.rotation || 0;
        if (
          isPointInRotationHandle(
            percentX,
            percentY,
            textX,
            textY,
            textBackgroundHeight,
            textRotation
          )
        ) {
          e.stopPropagation();
          setTextRotationState({
            isRotating: true,
            textOverlayId: textOverlay.id,
            startRotation: textOverlay.rotation || 0,
            startX: e.clientX,
            startY: e.clientY,
            centerX: textX,
            centerY: textY,
          });
          return;
        }
        const textCanvasHeight = textBackgroundHeight;
        if (
          isPointInRotationHandleCanvas(
            x,
            y,
            textCanvasPosition.x,
            textCanvasPosition.y,
            textCanvasHeight,
            textOverlay.rotation || 0,
            scaleX,
            scaleY,
            offsetX,
            offsetY
          )
        ) {
          e.stopPropagation();
          setTextRotationState({
            isRotating: true,
            textOverlayId: textOverlay.id,
            startRotation: textOverlay.rotation || 0,
            startX: e.clientX,
            startY: e.clientY,
            centerX: textX, // Use current text position
            centerY: textY, // Use current text position
          });
          return;
        }

        const corners = calculateRotatedCorners(
          textCanvasPosition.x,
          textCanvasPosition.y,
          textBackgroundWidth,
          textBackgroundHeight,
          textRotation
        );

        // Check if mouse is over any corner
        const clickedCorner = corners.find(
          (corner: { x: number; y: number }, index: number) => {
            const distance = Math.sqrt(
              (x - corner.x) ** 2 + (y - corner.y) ** 2
            );
            return distance <= RESIZE_HANDLE_SIZE / 2;
          }
        );

        if (clickedCorner) {
          const cornerNames = [
            "top-left",
            "top-right",
            "bottom-left",
            "bottom-right",
          ] as const;
          const cornerIndex = corners.indexOf(clickedCorner);
          e.stopPropagation();
          setTextResizeState({
            isResizing: true,
            textOverlayId: textOverlay.id,
            startFontSize: textOverlay.fontSize,
            startX: e.clientX,
            startY: e.clientY,
            corner: cornerNames[cornerIndex],
          });
          return;
        }

        // Check for drag clicks using screen coordinates
        // Account for rotation when checking hit detection
        const screenCoords = videoToScreenCoords(textX, textY);
        const dragBackgroundWidth = paddedWidth + 16;
        const dragBackgroundHeight = paddedHeight + 8;
        const dragRotation = textOverlay.rotation || 0;

        const isInside = isPointInRotatedRectangle(
          x,
          y,
          screenCoords.x,
          screenCoords.y,
          dragBackgroundWidth,
          dragBackgroundHeight,
          dragRotation
        );

        if (isInside) {
          if (showTextModal && handleTextEditCancel) {
            handleTextEditCancel();
          }
          // Always update selection first, even if another text is already selected
          // This ensures that clicking on a different text overlay always updates the selection
          onTextOverlaySelect?.(textOverlay.id);

          // Clear inline editing state when selecting a different text overlay
          // This prevents the caret from appearing on the wrong text
          if (
            inlineTextEditingState.isEditing &&
            inlineTextEditingState.textOverlayId !== textOverlay.id
          ) {
            setInlineTextEditingState({
              isEditing: false,
              textOverlayId: undefined,
              text: "",
              position: { x: 0, y: 0 },
              fontSize: 24,
              fontColor: "#ffffff",
              backgroundColor: "#000000",
              isBold: false,
              isItalic: false,
              isUnderline: false,
              padding: 0,
              rotation: 0,
              opacity: 1.0,
              fontFamily: "Arial",
            });
          }

          setDragState({
            isDragging: true,
            element: "text",
            textOverlayId: textOverlay.id,
            logoOverlayId: undefined,
            startX: e.clientX,
            startY: e.clientY,
            startPosition: { x: textX, y: textY },
          });
          return;
        }
      }
    }

    // Check logo overlays
    for (const logoOverlay of logoOverlays) {
      if (logoOverlay.visible && logoOverlay.url) {
        const logoX = logoOverlay.position.x;
        const logoY = logoOverlay.position.y;
        const logoWidth = (logoOverlay.size.width / videoWidth) * 100;
        const logoHeight = (logoOverlay.size.height / videoHeight) * 100;
        const handleSizePercent = (RESIZE_HANDLE_HIT_SIZE / videoWidth) * 100;
        const corners = getCornerPositions(logoX, logoY, logoWidth, logoHeight);
        const clickedCorner = corners.find(({ x: cornerX, y: cornerY }) =>
          isPointInCorner(
            percentX,
            percentY,
            cornerX,
            cornerY,
            handleSizePercent
          )
        );

        if (clickedCorner) {
          e.stopPropagation();
          setResizeState({
            isResizing: true,
            element: "logo",
            logoOverlayId: logoOverlay.id,
            startX: e.clientX,
            startY: e.clientY,
            startSize: { ...logoOverlay.size },
            corner: clickedCorner.corner,
          });
          return;
        }

        // Check for drag clicks using screen coordinates
        const screenCoords = videoToScreenCoords(logoX, logoY);
        const logoScreenWidth = (logoWidth / 100) * videoAreaWidth;
        const logoScreenHeight = (logoHeight / 100) * videoAreaHeight;

        if (
          isPointInOverlayArea(
            x,
            y,
            screenCoords.x,
            screenCoords.y,
            logoScreenWidth,
            logoScreenHeight
          )
        ) {
          onLogoOverlaySelect?.(logoOverlay.id);

          setDragState({
            isDragging: true,
            element: "logo",
            textOverlayId: undefined,
            logoOverlayId: logoOverlay.id,
            startX: e.clientX,
            startY: e.clientY,
            startPosition: { x: logoX, y: logoY },
          });
          return;
        }
      }
    }

    // Check slate overlays
    for (const slateOverlay of slateOverlays) {
      if (slateOverlay.visible && slateOverlay.url) {
        const slateX = slateOverlay.position.x;
        const slateY = slateOverlay.position.y;
        const slateWidth = (slateOverlay.size.width / videoWidth) * 100;
        const slateHeight = (slateOverlay.size.height / videoHeight) * 100;
        const handleSizePercent = (RESIZE_HANDLE_HIT_SIZE / videoWidth) * 100;
        const corners = getCornerPositions(
          slateX,
          slateY,
          slateWidth,
          slateHeight
        );
        const clickedCorner = corners.find(({ x: cornerX, y: cornerY }) =>
          isPointInCorner(
            percentX,
            percentY,
            cornerX,
            cornerY,
            handleSizePercent
          )
        );

        if (clickedCorner) {
          e.stopPropagation();
          setResizeState({
            isResizing: true,
            element: "slate",
            logoOverlayId: slateOverlay.id,
            startX: e.clientX,
            startY: e.clientY,
            startSize: { ...slateOverlay.size },
            corner: clickedCorner.corner,
          });
          return;
        }

        // Check for drag clicks using screen coordinates
        const screenCoords = videoToScreenCoords(slateX, slateY);
        const slateScreenWidth = (slateWidth / 100) * videoAreaWidth;
        const slateScreenHeight = (slateHeight / 100) * videoAreaHeight;

        if (
          isPointInOverlayArea(
            x,
            y,
            screenCoords.x,
            screenCoords.y,
            slateScreenWidth,
            slateScreenHeight
          )
        ) {
          setDragState({
            isDragging: true,
            element: "slate",
            textOverlayId: undefined,
            logoOverlayId: slateOverlay.id,
            startX: e.clientX,
            startY: e.clientY,
            startPosition: { x: slateX, y: slateY },
          });
          return;
        }
      }
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (
      !canvasRef.current ||
      !showCanvasOverlay ||
      readOnlyMode ||
      contentNonEditable
    )
      return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Get video dimensions for proper coordinate scaling
    const videoElement = document.querySelector("video");
    const videoWidth =
      videoDimensions?.width || videoElement?.videoWidth || 1920;
    const videoHeight =
      videoDimensions?.height || videoElement?.videoHeight || 1080;

    // Calculate scaling factors for proper coordinate conversion
    const canvasAspectRatio = rect.width / rect.height;
    const videoAspectRatio = videoWidth / videoHeight;

    let scaleX = 1;
    let scaleY = 1;
    let offsetX = 0;
    let offsetY = 0;

    if (canvasAspectRatio > videoAspectRatio) {
      // Canvas is wider than video (letterboxing)
      scaleY = rect.height / videoHeight;
      scaleX = scaleY;
      offsetX = (rect.width - videoWidth * scaleX) / 2;
    } else {
      // Canvas is taller than video (pillarboxing)
      scaleX = rect.width / videoWidth;
      scaleY = scaleX;
      offsetY = (rect.height - videoHeight * scaleY) / 2;
    }

    // Calculate the actual video area dimensions on the canvas
    const videoAreaWidth = videoWidth * scaleX;
    const videoAreaHeight = videoHeight * scaleY;

    // Convert canvas coordinates to video coordinates
    const videoX = (x - offsetX) / scaleX;
    const videoY = (y - offsetY) / scaleY;
    const percentX = Math.round((videoX / videoWidth) * 100);
    const percentY = Math.round((videoY / videoHeight) * 100);

    // Helper function to convert video coordinates to screen coordinates
    const videoToScreenCoords = (videoX: number, videoY: number) => {
      // Use the actual video content area dimensions, not the entire canvas
      const screenX = (videoX / 100) * videoAreaWidth + offsetX;
      const screenY = (videoY / 100) * videoAreaHeight + offsetY;
      return { x: screenX, y: screenY };
    };

    // Helper function to check if a point is within an overlay area (using screen coordinates)
    const isPointInOverlayArea = (
      pointX: number,
      pointY: number,
      overlayX: number,
      overlayY: number,
      overlayWidth: number,
      overlayHeight: number
    ) => {
      const tolerance = 50; // Increased tolerance for better detection, especially for overlays outside video area
      return (
        Math.abs(pointX - overlayX) < overlayWidth / 2 + tolerance &&
        Math.abs(pointY - overlayY) < overlayHeight / 2 + tolerance
      );
    };
    for (const textOverlay of textOverlays) {
      if (textOverlay.visible && textOverlay.text) {
        const textX = textOverlay.position.x;
        const textY = textOverlay.position.y;
        const fontSize = textOverlay.fontSize;
        const padding = (textOverlay.padding || 0) * Math.min(scaleX, scaleY);
        const maxWidth = videoWidth * Math.min(scaleX, scaleY);

        const tempCtx = createTextContext(textOverlay, fontSize);
        if (!tempCtx) continue;

        const { paddedWidth, paddedHeight } = calculateTextDimensions(
          tempCtx,
          textOverlay.text || "New Text",
          fontSize,
          padding,
          maxWidth
        );
        const backgroundWidth = paddedWidth + 16;
        const backgroundHeight = paddedHeight + 8;

        const screenCoords = videoToScreenCoords(textX, textY);
        const rotation = textOverlay.rotation || 0;

        // Account for rotation when checking hit detection for double-click
        const isInside = isPointInRotatedRectangle(
          x,
          y,
          screenCoords.x,
          screenCoords.y,
          backgroundWidth,
          backgroundHeight,
          rotation
        );

        if (isInside) {
          // Update selection when double-clicking to start inline editing
          // This ensures the selection state is in sync with the editing state
          onTextOverlaySelect?.(textOverlay.id);

          setInlineTextEditingState({
            isEditing: true,
            textOverlayId: textOverlay.id,
            text: textOverlay.text,
            position: { x: textX, y: textY },
            fontSize: textOverlay.fontSize,
            fontColor: textOverlay.fontColor,
            backgroundColor: textOverlay.backgroundColor,
            isBold: textOverlay.isBold || false,
            isItalic: textOverlay.isItalic || false,
            isUnderline: textOverlay.isUnderline || false,
            padding: textOverlay.padding || 0,
            rotation: textOverlay.rotation || 0,
            opacity: textOverlay.opacity || 1.0,
            fontFamily: textOverlay.fontFamily || "Arial",
          });
          return;
        }
      }
    }

    // Check for logo double-clicks
    for (const logoOverlay of logoOverlays) {
      if (logoOverlay.visible && logoOverlay.url) {
        const logoX = logoOverlay.position.x;
        const logoY = logoOverlay.position.y;
        const logoWidth = (logoOverlay.size.width / videoWidth) * 100;
        const logoHeight = (logoOverlay.size.height / videoHeight) * 100;

        // Check for double click using screen coordinates
        const screenCoords = videoToScreenCoords(logoX, logoY);
        const logoScreenWidth = (logoWidth / 100) * videoAreaWidth;
        const logoScreenHeight = (logoHeight / 100) * videoAreaHeight;

        if (
          isPointInOverlayArea(
            x,
            y,
            screenCoords.x,
            screenCoords.y,
            logoScreenWidth,
            logoScreenHeight
          )
        ) {
          // Call the external callback instead of setting the internal logo editing state
          if (onLogoOverlaySelect) {
            onLogoOverlaySelect(logoOverlay.id);
          } else {
            // Fallback to internal state management if no callback provided
            setLogoEditingState({
              isEditing: true,
              logoOverlayId: logoOverlay.id,
              url: logoOverlay.url,
              position: { x: logoX, y: logoY },
              size: logoOverlay.size,
              opacity: logoOverlay.opacity || 1.0,
              aspectRatio: logoOverlay.size.width / logoOverlay.size.height,
              rotation: logoOverlay.rotation || 0,
            });
            setShowLogoModal(true);
          }
          return;
        }
      }
    }
  };

  const handleDragMove = (e: MouseEvent) => {
    if (!dragState.isDragging || !canvasRef.current) {
      return;
    }
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    // Get video dimensions for proper coordinate scaling
    const videoElement = document.querySelector("video");
    const videoWidth =
      videoDimensions?.width || videoElement?.videoWidth || 1920;
    const videoHeight =
      videoDimensions?.height || videoElement?.videoHeight || 1080;

    // Calculate scaling factors for proper coordinate conversion (same as handleMouseDown)
    const canvasAspectRatio = rect.width / rect.height;
    const videoAspectRatio = videoWidth / videoHeight;

    let scaleX = 1;
    let scaleY = 1;
    let offsetX = 0;
    let offsetY = 0;

    if (canvasAspectRatio > videoAspectRatio) {
      // Canvas is wider than video (letterboxing)
      scaleY = rect.height / videoHeight;
      scaleX = scaleY;
      offsetX = (rect.width - videoWidth * scaleX) / 2;
    } else {
      // Canvas is taller than video (pillarboxing)
      scaleX = rect.width / videoWidth;
      scaleY = scaleX;
      offsetY = (rect.height - videoHeight * scaleY) / 2;
    }

    // Calculate the actual video area dimensions on the canvas
    const videoAreaWidth = videoWidth * scaleX;
    const videoAreaHeight = videoHeight * scaleY;

    // Calculate drag delta in video coordinates
    const deltaX = e.clientX - dragState.startX;
    const deltaY = e.clientY - dragState.startY;

    // Convert delta to video percentage coordinates
    const deltaPercentX = (deltaX / videoAreaWidth) * 100;
    const deltaPercentY = (deltaY / videoAreaHeight) * 100;

    let newX = dragState.startPosition.x + deltaPercentX;
    let newY = dragState.startPosition.y + deltaPercentY;

    if (dragState.element === "logo" && dragState.logoOverlayId) {
      const logoOverlay = logoOverlays.find(
        (logo) => logo.id === dragState.logoOverlayId
      );
      if (logoOverlay) {
        const logoWidth = (logoOverlay.size.width / videoWidth) * 100;
        const logoHeight = (logoOverlay.size.height / videoHeight) * 100;

        const minX = logoWidth / 2;
        const maxX = 100 - logoWidth / 2;
        const minY = logoHeight / 2;
        const maxY = 100 - logoHeight / 2;

        const constrainedX = Math.max(minX, Math.min(maxX, newX));
        const constrainedY = Math.max(minY, Math.min(maxY, newY));
        newX = constrainedX;
        newY = constrainedY;

        updateLogoOverlay(dragState.logoOverlayId, {
          position: { x: newX, y: newY },
        });
      }
    } else if (dragState.element === "slate" && dragState.logoOverlayId) {
      const slateOverlay = slateOverlays.find(
        (slate) => slate.id === dragState.logoOverlayId
      );
      if (slateOverlay) {
        const slateWidth = (slateOverlay.size.width / videoWidth) * 100;
        const slateHeight = (slateOverlay.size.height / videoHeight) * 100;

        // More restrictive boundaries for slate overlays to keep them within video area
        const minX = slateWidth / 2;
        const maxX = 100 - slateWidth / 2;
        const minY = slateHeight / 2;
        const maxY = 100 - slateHeight / 2;

        newX = Math.max(minX, Math.min(maxX, newX));
        newY = Math.max(minY, Math.min(maxY, newY));

        updateSlateOverlay(dragState.logoOverlayId, {
          position: { x: newX, y: newY },
        });
      }
    } else if (dragState.element === "text" && dragState.textOverlayId) {
      // For text overlays, use proper boundary constraints like other overlays
      const textOverlay = textOverlays.find(
        (text) => text.id === dragState.textOverlayId
      );
      if (textOverlay) {
        const tempCtx = createTextContext(textOverlay);
        if (tempCtx) {
          const maxWidth = videoWidth * scaleX;
          const padding = textOverlay.padding || 0;
          const textForCalculation = textOverlay.text || "New Text";

          const { paddedWidth, paddedHeight } = calculateTextDimensions(
            tempCtx,
            textForCalculation,
            textOverlay.fontSize,
            padding,
            maxWidth
          );
          const rotation = textOverlay.rotation || 0;
          const rotationRad = (rotation * Math.PI) / 180;
          const cosRot = Math.abs(Math.cos(rotationRad));
          const sinRot = Math.abs(Math.sin(rotationRad));
          const rotatedWidth = paddedWidth * cosRot + paddedHeight * sinRot;
          const rotatedHeight = paddedWidth * sinRot + paddedHeight * cosRot;
          const backgroundPaddingX = 16;
          const backgroundPaddingY = 8;
          const totalRotatedWidth = rotatedWidth + backgroundPaddingX;
          const totalRotatedHeight = rotatedHeight + backgroundPaddingY;

          const videoAreaWidth = videoWidth * scaleX;
          const videoAreaHeight = videoHeight * scaleY;
          const textWidthPercent = (totalRotatedWidth / videoAreaWidth) * 100;
          const textHeightPercent =
            (totalRotatedHeight / videoAreaHeight) * 100;
          const minX = textWidthPercent / 2 + 0.5; // Add 0.5% buffer
          const maxX = 100 - textWidthPercent / 2 - 0.5; // Subtract 0.5% buffer
          const minY = textHeightPercent / 2 + 0.5; // Add 0.5% buffer
          const maxY = 100 - textHeightPercent / 2 - 0.5; // Subtract 0.5% buffer

          newX = Math.max(minX, Math.min(maxX, newX));
          newY = Math.max(minY, Math.min(maxY, newY));

          updateTextOverlay(dragState.textOverlayId, {
            position: { x: newX, y: newY },
          });
        } else {
          const textForEstimation = textOverlay.text || "New Text";
          const estimatedTextWidth =
            ((textOverlay.fontSize * textForEstimation.length * 0.6) /
              videoWidth) *
            100;
          const estimatedTextHeight =
            (textOverlay.fontSize / videoHeight) * 100;

          const minX = estimatedTextWidth / 2;
          const maxX = 100 - estimatedTextWidth / 2;
          const minY = estimatedTextHeight / 2;
          const maxY = 100 - estimatedTextHeight / 2;

          newX = Math.max(minX, Math.min(maxX, newX));
          newY = Math.max(minY, Math.min(maxY, newY));

          updateTextOverlay(dragState.textOverlayId, {
            position: { x: newX, y: newY },
          });
        }
      }
    }
  };

  const handleMouseUp = () => {
    // Ensure all states are properly reset regardless of which state was active
    if (
      dragState.isDragging ||
      resizeState.isResizing ||
      textResizeState.isResizing ||
      textRotationState.isRotating
    ) {
      const draggedTextOverlayId = dragState.textOverlayId;

      // Add a small delay to prevent rapid state changes that might cause conflicts
      setTimeout(() => {
        setDragState({
          isDragging: false,
          element: null,
          textOverlayId: undefined,
          logoOverlayId: undefined,
          startX: 0,
          startY: 0,
          startPosition: { x: 0, y: 0 },
        });
        if (draggedTextOverlayId && onTextOverlaySelect) {
          onTextOverlaySelect(draggedTextOverlayId);
        }
        setResizeState({
          isResizing: false,
          element: null,
          logoOverlayId: undefined,
          startX: 0,
          startY: 0,
          startSize: { width: 0, height: 0 },
          corner: null,
        });
        setTextResizeState({
          isResizing: false,
          textOverlayId: undefined,
          startFontSize: 0,
          startX: 0,
          startY: 0,
          corner: null,
        });
        setTextRotationState({
          isRotating: false,
          textOverlayId: undefined,
          startRotation: 0,
          startX: 0,
          startY: 0,
          centerX: 0,
          centerY: 0,
        });
      }, 10);
    }
  };

  // Helper function to apply size constraints and boundary checks
  const applyResizeConstraints = (
    newWidth: number,
    newHeight: number,
    overlay: any,
    videoWidth: number,
    videoHeight: number,
    aspectRatio?: number
  ) => {
    const maxWidth = videoWidth * 0.8;
    const maxHeight = videoHeight * 0.8;

    if (aspectRatio && isFinite(aspectRatio) && aspectRatio > 0) {
      let constrainedWidth = Math.min(maxWidth, newWidth);
      constrainedWidth = Math.max(20, constrainedWidth);
      let constrainedHeight = constrainedWidth / aspectRatio;
      if (constrainedHeight > maxHeight) {
        constrainedHeight = Math.min(maxHeight, constrainedHeight);
        constrainedHeight = Math.max(20, constrainedHeight);
        constrainedWidth = constrainedHeight * aspectRatio;
        constrainedWidth = Math.min(maxWidth, constrainedWidth);
      }

      newWidth = constrainedWidth;
      newHeight = constrainedHeight;
    } else {
      newWidth = Math.min(maxWidth, newWidth);
      newHeight = Math.min(maxHeight, newHeight);
      newWidth = Math.max(20, newWidth);
      newHeight = Math.max(20, newHeight);
    }

    if (overlay) {
      // Convert percentage position to video coordinates (since canvas is sized to video)
      const overlayX = (overlay.position.x / 100) * videoWidth;
      const overlayY = (overlay.position.y / 100) * videoHeight;

      // Check if the resized overlay would go outside canvas boundaries
      const halfWidth = newWidth / 2;
      const halfHeight = newHeight / 2;

      if (aspectRatio && isFinite(aspectRatio) && aspectRatio > 0) {
        let maxAllowedWidth = videoWidth;
        let maxAllowedHeight = videoHeight;
        if (overlayX - halfWidth < 0 || overlayX + halfWidth > videoWidth) {
          maxAllowedWidth = Math.min(overlayX * 2, (videoWidth - overlayX) * 2);
        }
        if (overlayY - halfHeight < 0 || overlayY + halfHeight > videoHeight) {
          maxAllowedHeight = Math.min(
            overlayY * 2,
            (videoHeight - overlayY) * 2
          );
        }
        const widthFromHeight = maxAllowedHeight * aspectRatio;
        const heightFromWidth = maxAllowedWidth / aspectRatio;

        if (widthFromHeight <= maxAllowedWidth) {
          newWidth = Math.min(newWidth, widthFromHeight);
          newHeight = newWidth / aspectRatio;
        } else {
          newHeight = Math.min(newHeight, heightFromWidth);
          newWidth = newHeight * aspectRatio;
        }
      } else {
        if (overlayX - halfWidth < 0 || overlayX + halfWidth > videoWidth) {
          const maxAllowedWidth = Math.min(
            overlayX * 2,
            (videoWidth - overlayX) * 2
          );
          newWidth = Math.min(newWidth, maxAllowedWidth);
        }

        if (overlayY - halfHeight < 0 || overlayY + halfHeight > videoHeight) {
          const maxAllowedHeight = Math.min(
            overlayY * 2,
            (videoHeight - overlayY) * 2
          );
          newHeight = Math.min(newHeight, maxAllowedHeight);
        }
      }
    }

    return { width: newWidth, height: newHeight };
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (!resizeState.isResizing || !canvasRef.current) {
      return;
    }
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const deltaX = e.clientX - resizeState.startX;
    const deltaY = e.clientY - resizeState.startY;

    // Get video dimensions for proper constraint calculations
    const videoElement = document.querySelector("video");
    const videoWidth =
      videoDimensions?.width || videoElement?.videoWidth || 1920;
    const videoHeight =
      videoDimensions?.height || videoElement?.videoHeight || 1080;

    if (resizeState.element === "logo" && resizeState.logoOverlayId) {
      let newWidth = resizeState.startSize.width;
      let newHeight = resizeState.startSize.height;

      const currentOverlay = logoOverlays.find(
        (overlay) => overlay.id === resizeState.logoOverlayId
      );

      let aspectRatio: number | undefined = undefined;
      if (maintainAspectRatio && currentOverlay) {
        const storedAspectRatio = (currentOverlay as any).aspectRatio;
        aspectRatio =
          storedAspectRatio &&
          isFinite(storedAspectRatio) &&
          storedAspectRatio > 0
            ? storedAspectRatio
            : resizeState.startSize.width / resizeState.startSize.height;

        const primaryDelta =
          Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;
        let effectiveDelta = 0;
        switch (resizeState.corner) {
          case "top-left":
            effectiveDelta = -primaryDelta;
            break;
          case "top-right":
            effectiveDelta =
              Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : -deltaY;
            break;
          case "bottom-left":
            effectiveDelta =
              Math.abs(deltaX) > Math.abs(deltaY) ? -deltaX : deltaY;
            break;
          case "bottom-right":
            effectiveDelta = primaryDelta;
            break;
        }

        newWidth = Math.max(
          20,
          resizeState.startSize.width + effectiveDelta * 2
        );
        newHeight = newWidth / (aspectRatio || 1);
      } else {
        switch (resizeState.corner) {
          case "top-left":
            newWidth = Math.max(20, resizeState.startSize.width - deltaX * 2);
            newHeight = Math.max(20, resizeState.startSize.height - deltaY * 2);
            break;
          case "top-right":
            newWidth = Math.max(20, resizeState.startSize.width + deltaX * 2);
            newHeight = Math.max(20, resizeState.startSize.height - deltaY * 2);
            break;
          case "bottom-left":
            newWidth = Math.max(20, resizeState.startSize.width - deltaX * 2);
            newHeight = Math.max(20, resizeState.startSize.height + deltaY * 2);
            break;
          case "bottom-right":
            newWidth = Math.max(20, resizeState.startSize.width + deltaX * 2);
            newHeight = Math.max(20, resizeState.startSize.height + deltaY * 2);
            break;
        }
      }

      const constrainedSize = applyResizeConstraints(
        newWidth,
        newHeight,
        currentOverlay,
        videoWidth,
        videoHeight,
        aspectRatio
      );

      updateLogoOverlay(resizeState.logoOverlayId, { size: constrainedSize });
    } else if (resizeState.element === "slate" && resizeState.logoOverlayId) {
      let newWidth = resizeState.startSize.width;
      let newHeight = resizeState.startSize.height;
      switch (resizeState.corner) {
        case "top-left":
          newWidth = Math.max(20, resizeState.startSize.width - deltaX * 2);
          newHeight = Math.max(20, resizeState.startSize.height - deltaY * 2);
          break;
        case "top-right":
          newWidth = Math.max(20, resizeState.startSize.width + deltaX * 2);
          newHeight = Math.max(20, resizeState.startSize.height - deltaY * 2);
          break;
        case "bottom-left":
          newWidth = Math.max(20, resizeState.startSize.width - deltaX * 2);
          newHeight = Math.max(20, resizeState.startSize.height + deltaY * 2);
          break;
        case "bottom-right":
          newWidth = Math.max(20, resizeState.startSize.width + deltaX * 2);
          newHeight = Math.max(20, resizeState.startSize.height + deltaY * 2);
          break;
      }

      // Apply constraints using helper function
      const currentOverlay = slateOverlays.find(
        (overlay) => overlay.id === resizeState.logoOverlayId
      );
      const constrainedSize = applyResizeConstraints(
        newWidth,
        newHeight,
        currentOverlay,
        videoWidth,
        videoHeight
      );

      updateSlateOverlay(resizeState.logoOverlayId, { size: constrainedSize });
    }
  };

  const handleTextResizeMove = (e: MouseEvent) => {
    if (!textResizeState.isResizing || !textResizeState.textOverlayId) {
      return;
    }

    const deltaX = e.clientX - textResizeState.startX;
    const deltaY = e.clientY - textResizeState.startY;

    // Calculate the scale factor based on the corner being dragged
    let scaleFactor = 1;
    switch (textResizeState.corner) {
      case "top-left":
        scaleFactor = 1 + (-deltaX - deltaY) / 100;
        break;
      case "top-right":
        scaleFactor = 1 + (deltaX - deltaY) / 100;
        break;
      case "bottom-left":
        scaleFactor = 1 + (-deltaX + deltaY) / 100;
        break;
      case "bottom-right":
        scaleFactor = 1 + (deltaX + deltaY) / 100;
        break;
    }

    let newFontSize = Math.max(
      8,
      Math.min(72, textResizeState.startFontSize * scaleFactor)
    );

    if (videoDimensions) {
      const videoWidth = videoDimensions.width;
      const videoHeight = videoDimensions.height;
      const maxFontSizeByHeight = videoHeight * 0.12; // 12% of video height
      const maxFontSizeByWidth = videoWidth * 0.06; // 6% of video width
      const maxFontSize = Math.min(maxFontSizeByHeight, maxFontSizeByWidth, 64); // Balanced max limit
      newFontSize = Math.min(newFontSize, maxFontSize);
    }
    updateTextOverlay(
      textResizeState.textOverlayId,
      { fontSize: newFontSize },
      true
    );

    // Force immediate canvas re-render to update resize handle positions
    if (renderCanvas) {
      renderCanvas();
    }

    // Update cursor to show resize cursor during resize operation
    if (canvasRef.current && textResizeState.corner) {
      const canvas = canvasRef.current;
      const cornerCursors = {
        "top-left": "nw-resize",
        "top-right": "ne-resize",
        "bottom-left": "sw-resize",
        "bottom-right": "se-resize",
      };
      canvas.style.cursor =
        cornerCursors[textResizeState.corner] || "se-resize";
    }
  };

  const handleRotationMove = (e: MouseEvent) => {
    if (!textRotationState.isRotating || !textRotationState.textOverlayId) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const videoElement = document.querySelector("video");
    const videoWidth =
      videoDimensions?.width || videoElement?.videoWidth || 1920;
    const videoHeight =
      videoDimensions?.height || videoElement?.videoHeight || 1080;

    // Calculate scaling factors for proper coordinate conversion
    const canvasAspectRatio = rect.width / rect.height;
    const videoAspectRatio = videoWidth / videoHeight;

    let scaleX = 1;
    let scaleY = 1;
    let offsetX = 0;
    let offsetY = 0;

    if (canvasAspectRatio > videoAspectRatio) {
      scaleY = rect.height / videoHeight;
      scaleX = scaleY;
      offsetX = (rect.width - videoWidth * scaleX) / 2;
    } else {
      scaleX = rect.width / videoWidth;
      scaleY = scaleX;
      offsetY = (rect.height - videoHeight * scaleY) / 2;
    }

    // Convert mouse position to video coordinates
    const mouseX = (e.clientX - offsetX) / scaleX;
    const mouseY = (e.clientY - offsetY) / scaleY;
    const mousePercentX = (mouseX / videoWidth) * 100;
    const mousePercentY = (mouseY / videoHeight) * 100;

    // Calculate the center position in canvas coordinates
    const centerCanvasX =
      (textRotationState.centerX / 100) * videoWidth * scaleX + offsetX;
    const centerCanvasY =
      (textRotationState.centerY / 100) * videoHeight * scaleY + offsetY;

    // Calculate angle between center and current mouse position
    const deltaX = e.clientX - centerCanvasX;
    const deltaY = e.clientY - centerCanvasY;
    const currentAngle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);

    // Calculate angle between center and starting mouse position
    const startDeltaX = textRotationState.startX - centerCanvasX;
    const startDeltaY = textRotationState.startY - centerCanvasY;
    const startAngle = Math.atan2(startDeltaY, startDeltaX) * (180 / Math.PI);

    // Calculate the rotation delta
    let angleDelta = currentAngle - startAngle;

    // Handle angle wrapping (when crossing 180/-180 degrees)
    if (angleDelta > 180) angleDelta -= 360;
    if (angleDelta < -180) angleDelta += 360;

    // Apply the rotation delta to the starting rotation
    let newRotation = textRotationState.startRotation + angleDelta;

    // Keep rotation between 0 and 360 degrees
    while (newRotation < 0) newRotation += 360;
    while (newRotation >= 360) newRotation -= 360;

    updateTextOverlay(textRotationState.textOverlayId, {
      rotation: newRotation,
    });
  };

  // Event listeners
  useEffect(() => {
    if (dragState.isDragging) {
      document.addEventListener("mousemove", handleDragMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleDragMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [dragState]);

  useEffect(() => {
    if (resizeState.isResizing) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [resizeState]);

  useEffect(() => {
    if (textResizeState.isResizing) {
      document.addEventListener("mousemove", handleTextResizeMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleTextResizeMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [textResizeState.isResizing, textResizeState.textOverlayId]);

  useEffect(() => {
    if (textRotationState.isRotating) {
      document.addEventListener("mousemove", handleRotationMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleRotationMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [textRotationState.isRotating, textRotationState.textOverlayId]);

  return {
    handleMouseDown,
    handleCursorMove,
    handleDoubleClick,
    handleDragMove,
    handleMouseUp,
    handleResizeMove,
    handleTextResizeMove,
    handleRotationMove,
    getCornerPositions,
    isPointInCorner,
    isPointInLogo,
    CORNER_CURSORS,
    RESIZE_HANDLE_SIZE,
  };
};
